import { parseISO } from "date-fns";
import type { Maybe } from "@entrilia/shared/types";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ClientDictionaries } from "../../api/types/clientTypes";
import type { Field, ObjectWithFieldValuesBase } from "../../api/types/objectTypes";
import { getDictionaryDisplayNameFromCode } from "../../utilities/dictionariesHelper";
import {
  getColumnDefinitionForField,
  getColumnDeinitionsForTimestampFields,
} from "../common/columns/columnDefinitions";
import type { SearchFilterDefinition, TableFilterDefinition } from "../common/filters/filterTypes";
import { getLookupReferenceForFieldId, getMultiselectOptionsFromValue, isFieldFilterable } from "./helpers";

export const getFilterDefinitionForEntityField = <R extends ObjectWithFieldValuesBase>(
  field: Field,
  dictionaries: ClientDictionaries
): TableFilterDefinition<R> => {
  const { id, type, name, configuration } = field;
  const filterDefinition = { id, name, dynamic: true };

  switch (type) {
    case "Number":
    case "Money":
    case "Percent": {
      return {
        ...filterDefinition,
        type: "number",
        getFieldValue: (row) => {
          const value = row.fieldValues[id];
          return value ? Number(value) : undefined;
        },
      };
    }
    case "Checkbox": {
      return {
        ...filterDefinition,
        type: "boolean",
        getFieldValue: (row) => !!row.fieldValues[id],
      };
    }
    case "Date":
    case "DateTime": {
      return {
        ...filterDefinition,
        type: "date",
        getFieldValue: (row) => {
          const value = row.fieldValues[id];
          return value ? parseISO(stringifyValue(value)) : undefined;
        },
      };
    }
    case "Select": {
      return {
        ...filterDefinition,
        type: "multi_select",
        getFieldValue: (row) => {
          const fieldValue = row.fieldValues[id]?.toString();
          if (!fieldValue) {
            return undefined;
          }
          const optionFieldType = configuration?.$type === "Select" && configuration.fieldType;
          return (
            (optionFieldType && getDictionaryDisplayNameFromCode(fieldValue, optionFieldType, dictionaries)) ||
            fieldValue
          );
        },
      };
    }
    case "UserDefinedOptionsSelect": {
      return {
        ...filterDefinition,
        type: "multi_select",
        getFieldValue: (row) => row.fieldValues[id]?.toString(),
      };
    }
    case "MultiSelect":
    case "UserDefinedOptionsMultiSelect": {
      return {
        ...filterDefinition,
        type: "multi_select",
        getFieldValue: (row) => getMultiselectOptionsFromValue(row.fieldValues[id] as Maybe<string | string[]>),
      };
    }
    case "Lookup": {
      return {
        ...filterDefinition,
        type: "multi_select",
        getFieldValue: (row) => {
          const lookupReference = getLookupReferenceForFieldId(id, row);
          if (!lookupReference) {
            return undefined;
          }

          return lookupReference.secondaryFieldValue
            ? [lookupReference.primaryFieldValue, lookupReference.secondaryFieldValue]
            : lookupReference.primaryFieldValue;
        },
      };
    }
    default: {
      return {
        ...filterDefinition,
        type: "text",
        getFieldValue: (row) => row.fieldValues[id]?.toString(),
      };
    }
  }
};

const getEntitySearchDefinition = <R extends ObjectWithFieldValuesBase>(
  fieldIds: string[]
): SearchFilterDefinition<R> => ({
  getFieldValues: (row) => fieldIds.map((fieldId) => row.fieldValues[fieldId]?.toString()).concat(row.id),
});

export const getEntityFilterDefinitions = (
  allFields: Field[],
  defaultGridFieldIds: string[],
  showTimestampsByDefault: boolean | undefined,
  searchFieldIds: string[],
  dictionaries: ClientDictionaries
) => {
  const columnDefinitions = allFields.map((field) =>
    getColumnDefinitionForField(field, defaultGridFieldIds.includes(field.id))
  );

  columnDefinitions.push(...getColumnDeinitionsForTimestampFields(showTimestampsByDefault));

  const filterDefinitions = allFields
    .filter(isFieldFilterable)
    .map((field) => getFilterDefinitionForEntityField(field, dictionaries));

  const searchDefinition = getEntitySearchDefinition(searchFieldIds);
  return { columnDefinitions, filterDefinitions, searchDefinition };
};
